"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import {
  hasAccountFlags,
  isNewsBellEligible,
  NEWS_BELL_FLAG_KEY,
  NEWS_BELL_PREVIEW_PARAM,
  NewsBellVariant,
  newsBellVariantFor,
} from "../helpers/newsBellExperiment";
import { useProfile } from "./useProfile";

const OVERRIDE_FEATURE_FLAGS_KEY = "$override_feature_flags";

// The signed-in user's arm of the "news-bell" experiment (see
// helpers/newsBellExperiment.ts). Returns undefined until it is decided, so
// the bell never flashes in or out.
// - Only mounted by UserNav's signed-in header, so an exposed user is one who
//   loaded a page with the app header, in both arms.
// - Exposure is the $feature_flag_called that getFeatureFlag records.
// - Waits for flags evaluated for the account (hasAccountFlags): on a
//   browser's first sign-in the flags first load for the anonymous id, which
//   can hash into the other arm. Only the account's arm is shown and recorded.
// - `?news_bell=test|control` skips the flag (no exposure), and so does local
//   dev, which shows the bell unless overridden from the console:
//     posthog.featureFlags.overrideFeatureFlags({ flags: { "news-bell": "control" } })
export const useNewsBellVariant = (): NewsBellVariant | undefined => {
  const posthog = usePostHog();
  const { data: profile } = useProfile();
  const [variant, setVariant] = useState<NewsBellVariant>();

  const isEligible = isNewsBellEligible(profile);
  const profileId = profile?.id;

  useEffect(() => {
    const preview = new URLSearchParams(window.location.search).get(
      NEWS_BELL_PREVIEW_PARAM,
    );
    if (preview) {
      setVariant(newsBellVariantFor(preview));
      return;
    }
    if (!posthog || !isEligible || !profileId) return;

    if (process.env.NODE_ENV === "development") {
      const update = () => {
        const overrides = posthog.get_property?.(OVERRIDE_FEATURE_FLAGS_KEY) as
          | Record<string, string | boolean>
          | undefined;
        setVariant(newsBellVariantFor(overrides?.[NEWS_BELL_FLAG_KEY] ?? "test"));
      };
      update();
      return posthog.onFeatureFlags(update);
    }

    return posthog.onFeatureFlags(() => {
      if (!hasAccountFlags(posthog, profileId)) return;
      setVariant(newsBellVariantFor(posthog.getFeatureFlag(NEWS_BELL_FLAG_KEY)));
    });
  }, [posthog, isEligible, profileId]);

  return variant;
};
